/**
 * Pure refresh-auth helpers (testable without axios/vue).
 * Rotation: one in-flight refresh shared across concurrent 401s.
 */

export function extractBearerToken(headers = {}) {
  const raw =
    headers.Authorization ||
    headers.authorization ||
    (typeof headers.get === 'function'
      ? headers.get('Authorization') || headers.get('authorization')
      : null)
  if (!raw || typeof raw !== 'string') return null
  const m = raw.match(/^Bearer\s+(.+)$/i)
  return m ? m[1].trim() : null
}

export function shouldAttemptRefresh(error, { refreshToken, isRefreshRequest } = {}) {
  if (!error?.response) return false
  if (error.response.status !== 401) return false
  if (isRefreshRequest) return false
  if (error.config?._retry) return false
  if (!refreshToken) return false
  return true
}

export function isAuthRefreshUrl(url = '') {
  const path = String(url || '')
  return (
    path.includes('/auth/refresh') ||
    path.includes('/auth/logout') ||
    /\/auth\/token(?:\?|$)/.test(path) ||
    path.endsWith('/auth/token')
  )
}

/**
 * Creates a single-flight refresh runner.
 * @param {() => Promise<{access_token:string, refresh_token?:string, token_type?:string, expires_in?:number, _used_refresh?:string}>} refreshFn
 */
export function createRefreshGate(refreshFn) {
  let inflight = null

  return async function refreshOnce() {
    if (!inflight) {
      inflight = Promise.resolve()
        .then(() => refreshFn())
        .finally(() => {
          inflight = null
        })
    }
    return inflight
  }
}

export function applyBearerHeader(config, accessToken) {
  const next = { ...(config || {}) }
  const prevHeaders = next.headers || {}
  // Axios 1.x may use AxiosHeaders — prefer .set when available.
  if (prevHeaders && typeof prevHeaders.set === 'function') {
    prevHeaders.set('Authorization', `Bearer ${accessToken}`)
    next.headers = prevHeaders
  } else {
    next.headers = { ...prevHeaders, Authorization: `Bearer ${accessToken}` }
  }
  next._retry = true
  return next
}

/**
 * Decide whether failed refresh should wipe local auth.
 * Do NOT clear if storage already holds a different refresh (another tab rotated).
 */
export function shouldClearAuthAfterRefreshFailure(attemptedRefresh, currentRefresh) {
  if (!currentRefresh) return true
  if (!attemptedRefresh) return true
  return currentRefresh === attemptedRefresh
}

/**
 * Decide next step after 401 from an API call.
 * @returns {{action:'reject'|'retry_with_access'|'refresh', accessToken?: string}}
 */
export function planUnauthorizedRecovery({
  status,
  alreadyRetried = false,
  requestAccess = null,
  latestAccess = null,
  refreshToken = null,
  requestUrl = '',
  impersonating = false,
} = {}) {
  if (status !== 401) {
    return { action: 'reject' }
  }
  // Вкладка имперсонализации: 401 = конец сессии целевого пользователя.
  // Запрещаем фолбэк на токен из localStorage (это токен АДМИНА) и refresh
  // его refresh-токеном — иначе молча подменяется личность и UI показывает
  // учётку администратора с его правами.
  if (impersonating) {
    return { action: 'reject' }
  }
  if (
    !alreadyRetried &&
    latestAccess &&
    requestAccess &&
    latestAccess !== requestAccess
  ) {
    return { action: 'retry_with_access', accessToken: latestAccess }
  }
  const tryRefresh = shouldAttemptRefresh(
    { response: { status: 401 }, config: { _retry: alreadyRetried } },
    {
      refreshToken,
      isRefreshRequest: isAuthRefreshUrl(requestUrl),
    },
  )
  if (!tryRefresh) {
    return { action: 'reject' }
  }
  return { action: 'refresh' }
}

/**
 * @returns {{action:'clear_and_reject'|'retry_with_access'|'reject', accessToken?: string}}
 */
export function planAfterRefreshFailure({
  attemptedRefresh = null,
  currentRefresh = null,
  currentAccess = null,
} = {}) {
  if (shouldClearAuthAfterRefreshFailure(attemptedRefresh, currentRefresh)) {
    return { action: 'clear_and_reject' }
  }
  if (currentAccess) {
    return { action: 'retry_with_access', accessToken: currentAccess }
  }
  return { action: 'reject' }
}

/**
 * Execute refresh; on 409 with updated storage, reuse tokens from another tab.
 */
export async function executeRefreshAttempt({
  getRefreshToken,
  getAccessToken,
  writeStoredAuth,
  postRefresh,
}) {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    const err = new Error('No refresh token')
    err._used_refresh = null
    throw err
  }
  try {
    const data = await postRefresh(refreshToken)
    writeStoredAuth(data)
    return { ...data, _used_refresh: refreshToken }
  } catch (err) {
    if (err?.response?.status === 409) {
      const access = getAccessToken()
      const refresh = getRefreshToken()
      if (access && refresh && refresh !== refreshToken) {
        return {
          access_token: access,
          refresh_token: refresh,
          token_type: 'bearer',
          _used_refresh: refreshToken,
        }
      }
    }
    err._used_refresh = refreshToken
    throw err
  }
}

// Кросс-оконная координация refresh через Web Locks API: одновременно
// во всём браузере рефрешит только одно окно; остальные, дождавшись локa,
// видят в storage уже ротированный токен и не ходят в сеть.
export const AUTH_REFRESH_LOCK_NAME = 'auth:refresh'

export function supportsWebLocks(locksApi = globalThis.navigator?.locks) {
  return typeof locksApi?.request === 'function'
}

/**
 * @param {{
 *   observedRefreshToken: string|null,  // refresh-токен на момент 401
 *   doRefresh: () => Promise<object>,   // POST /auth/refresh + writeStoredAuth
 *   getRefreshToken: () => string|null, // перечитать storage (после ожидания лока)
 *   getAccessToken: () => string|null,
 * }} args
 * @returns {Promise<{access_token: string, refresh_token?: string, _adopted?: boolean}>}
 */
export async function refreshUnderCrossTabLock({
  locks = null,
  lockName = AUTH_REFRESH_LOCK_NAME,
  observedRefreshToken = null,
  doRefresh,
  getRefreshToken,
  getAccessToken,
}) {
  if (!supportsWebLocks(locks)) return doRefresh()
  const run = async () => {
    const currentRefresh = getRefreshToken()
    if (
      observedRefreshToken &&
      currentRefresh &&
      currentRefresh !== observedRefreshToken
    ) {
      // Кто-то ротировал токен, пока мы ждали лок, — берём новую пару из storage.
      const access = getAccessToken()
      if (access) {
        return {
          access_token: access,
          refresh_token: currentRefresh,
          token_type: 'bearer',
          _adopted: true,
        }
      }
    }
    return doRefresh()
  }
  return locks.request(lockName, { mode: 'exclusive' }, run)
}
